import { useCallback, useEffect } from 'react';
import { useGlobalSearchParams, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useBagCount } from '../../hooks';
import { useCartHandoffMutation } from './graphql/CartHandoff.generated';

/**
 * Hook to handle cart handoff from a URL parameter.
 * If successful, removes the orderToken from the URL and navigates to the bag screen.
 * @see {@link https://sweetgreen.atlassian.net/wiki/spaces/TD/pages/7249297413/Agentic+Ordering}
 */
export const useAutoCartHandoff = () => {
  const isFeatureFlagEnabled = useFeatureFlag('CELS-5511-cart-handoff-enabled');
  const { orderToken } = useGlobalSearchParams<{ orderToken?: string }>();
  const shouldQueryBagCount = Boolean(orderToken) && isFeatureFlagEnabled;

  const { handleCartHandoff } = useCartHandoff();
  const { bagCount, isLoadingBagCount } = useBagCount({
    requestPolicy: 'network-only',
    pause: !shouldQueryBagCount,
  });

  useEffect(() => {
    if (!isFeatureFlagEnabled || !orderToken || isLoadingBagCount || bagCount > 0) return;

    void handleCartHandoff(orderToken);
  }, [handleCartHandoff, isFeatureFlagEnabled, orderToken, isLoadingBagCount, bagCount]);
};

export const useCartHandoff = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner, remove: removeNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();

  const [response, handoffCart] = useCartHandoffMutation();
  const isHandingOffCart = response.fetching;

  const handleCartHandoff = useCallback(
    async (token: string) => {
      track('app.cart-handoff.start', { orderToken: token });

      addNoticeBanner({
        id: LOADING_BANNER_ID,
        text: formatMessage(messages.loading),
        palette: 'neutral',
        autoHideTimeout: 'never',
        withCloseBtn: false,
      });

      const result = await handoffCart({ orderToken: token });
      const hasHandoffCart = Boolean(result.data?.handoffCart?.id);

      removeNoticeBanner(LOADING_BANNER_ID);

      if (hasHandoffCart) {
        track('app.cart-handoff.success', { orderToken: token });
        router.setParams({ orderToken: undefined });
        router.navigate('/bag');
        return;
      }

      track('app.cart-handoff.failure', { orderToken: token });
      addNoticeBanner({
        text: formatMessage(messages.error),
        palette: 'caution',
      });
    },
    [addNoticeBanner, removeNoticeBanner, formatMessage, handoffCart, router, track],
  );

  return { handleCartHandoff, isHandingOffCart };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LOADING_BANNER_ID = 'cart-handoff-loading';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  loading: {
    defaultMessage: 'Setting up your order...',
    description: 'Cart Handoff | Loading',
  },
  error: {
    defaultMessage: 'Something went wrong setting up your order, please try again.',
    description: 'Cart Handoff | Error',
  },
});
